import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "../../redux/hooks";

interface Props {
  id: string;
  defaultValue: number | undefined;
  tag: string | undefined;
}
type OilOperation = "oilAdded" | "oilUsed";
type OilLocation = "PORT" | "CENTRE" | "STBD" | "";

type OilClassName = `${OilOperation}${OilLocation}`;

/**
 * Custom hook to calculate and update Arbema oil calculations.
 *
 * @param props - An object containing the following properties:
 *   - id: A string representing the unique identifier for the input.
 *   - tag: tag that sits in input obj.
 *   - defaultValue:default value of oil that will be substracted from or added to.

 */
const useArbemaTotals = ({ id, tag, defaultValue }: Props) => {
  const { setValue } = useFormContext();
  const engineSelectData = useSelector(
    state => state.generatorFeaturesSlice.engineSelectData,
  );

  useEffect(() => {
    const applyCallbackToOilItems = (
      className: OilClassName,
      callback: (item: any) => void,
    ) =>
      document
        .querySelectorAll(`.${className}`)
        .forEach(item => callback(item.querySelector("input")));

    const handleItemChange = (className: OilClassName) => {
      let total = defaultValue ? Number(defaultValue) : 0;
      const multiplier = className.startsWith("oilAdded") ? 1 : -1;
      applyCallbackToOilItems(className, item => {
        const value = item.value;
        if (value) {
          total += multiplier * Number(value);
        }
      });

      setValue(id, total);
    };

    let className: OilClassName | null = null;

    if (tag?.startsWith("arbemaTotalOilMonth")) {
      const location = tag.replace("arbemaTotalOilMonth", "").trim();
      switch (location) {
        case "PORT":
          className = "oilAddedPORT";
          break;
        case "CENTRE":
          className = "oilAddedCENTRE";
          break;
        case "STBD":
          className = "oilAddedSTBD";
          break;
      }
    } else if (tag?.startsWith("arbemaTotalOilUsed")) {
      const location = tag.replace("arbemaTotalOilUsed", "").trim();
      switch (location) {
        case "PORT":
          className = "oilUsedPORT";
          break;
        case "CENTRE":
          className = "oilUsedCENTRE";
          break;
        case "STBD":
          className = "oilUsedSTBD";
          ``;
          break;
        default:
          className = "oilUsed";
      }
    }
    if (!className) return;

    // Initial calculation
    handleItemChange(className);

    // Add event listeners for input changes
    applyCallbackToOilItems(className, item =>
      item.addEventListener("change", () => handleItemChange(className)),
    );

    return () => {
      if (!className) return;
      applyCallbackToOilItems(className, item =>
        item.removeEventListener("change", () => handleItemChange(className)),
      );
    };
  }, [id, tag, defaultValue, setValue, engineSelectData]);
};

export { useArbemaTotals };
